import React, { MutableRefObject, useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { useFormValidationErrorTracker } from '../../../../shared/analytics/form/useFormValidationErrorTracker';
import { IBooking } from '../../../domain/booking/models/IBooking';
import { INewBooking } from '../../../domain/booking/models/INewBooking';

import { DetailsSummarySubmitButton } from './DetailsSummarySubmitButton';
import { trackDetailsValidationErrors } from '../shared/analytics/custom/trackDetailsValidationErrors';
import { trackViewDetails } from '../shared/analytics/e-commerce/trackViewDetails';
import { BookingSummaryLayout } from '../shared/summary/BookingSummaryLayout';
import { DetailsLayout } from './DetailsLayout';
import { useDetailsForm } from './hooks/useDetailsForm';
import { FinalSubmitHandler, useDetailsSubmit } from './hooks/useDetailsSubmit';
import { DetailsDefaultValues } from './types/IDetailsFormData';
import { EmailSuggestionStore } from '../../../domain/email-validation/stores/EmailSuggestionStore';

interface IDetailsProps {
	formRef?: MutableRefObject<HTMLFormElement>;
	defaultValues?: DetailsDefaultValues;
	finalSubmitHandler: FinalSubmitHandler;
	submitButtonText: string;
	submitError?: Error;
	hasHiddenConsentCheckbox?: boolean;
	hasHiddenRememberDetailsCheckbox?: boolean;
	originalBooking?: IBooking;
	booking?: INewBooking;
	changeButtonUrl?: string;
	emailSuggestionStore?: EmailSuggestionStore;
	modifyBooking?: boolean;
}

export const Details = ({
	formRef,
	defaultValues,
	finalSubmitHandler,
	submitButtonText,
	submitError,
	hasHiddenConsentCheckbox,
	hasHiddenRememberDetailsCheckbox,
	originalBooking,
	booking,
	changeButtonUrl,
	emailSuggestionStore,
	modifyBooking,
}: IDetailsProps) => {
	const form = useDetailsForm({
		defaultValues,
		requiresConsent: !hasHiddenConsentCheckbox,
	});

	const { signUpError, saveDetailsError, submit } = useDetailsSubmit({
		finalSubmitHandler,
	});

	useEffect(() => {
		trackViewDetails();
	}, []);

	useFormValidationErrorTracker(form, trackDetailsValidationErrors);

	return (
		<FormProvider {...form}>
			<form
				ref={formRef}
				onSubmit={form.handleSubmit(submit, (errors) => {
					// eslint-disable-next-line no-console
					console.error('Form submission failed', errors, form.getValues());
				})}
			>
				{' '}
				<BookingSummaryLayout
					originalBooking={originalBooking}
					booking={booking}
					Submit={DetailsSummarySubmitButton}
					changeButtonUrl={changeButtonUrl}
				>
					<DetailsLayout
						signUpError={signUpError}
						saveDetailsError={saveDetailsError}
						submitError={submitError}
						submitButtonText={submitButtonText}
						hasHiddenConsentCheckbox={hasHiddenConsentCheckbox}
						hasHiddenRememberDetailsCheckbox={hasHiddenRememberDetailsCheckbox}
						emailSuggestionStore={emailSuggestionStore}
						modifyBooking={modifyBooking}
						bookingLicensePlate={defaultValues?.licensePlateNumber}
					/>
				</BookingSummaryLayout>
			</form>
		</FormProvider>
	);
};
